/**
 * Cache-first resolve for route loaders — a thin adapter over the data plane.
 *
 * The staleness decision lives in `@/lib/queries/data-plane` (`isStale`), so a
 * loader and a prefetcher can never disagree about whether an entry is usable.
 */
import type { QueryClient } from '@tanstack/react-query';
import { readCritical, type PlaneQuery } from './data-plane';

/**
 * CRITICAL read for a loader.
 *
 * `cached` is present whenever the cache holds the entry — stale data is
 * returned immediately and healed in the background, so a back-navigation or
 * re-entry never waits on a refetch it already has content for. A cold cache
 * returns `cached: undefined` and the caller streams `stream()` into its
 * `<Await>` boundary.
 */
export function cacheFirst<T>(
  client: QueryClient,
  queryKey: readonly unknown[],
  refresh: () => Promise<T>,
  opts?: { staleTime?: number }
): { cached: T | undefined; stream: () => Promise<T> } {
  const { cached, stream } = readCritical(
    client,
    { queryKey, queryFn: refresh } as PlaneQuery,
    opts
  );
  return {
    cached: cached as T | undefined,
    stream: stream as () => Promise<T>,
  };
}
